import type { DisplayMode } from "~/helpers/displayModePreference";

import { Box, Button, Chip, FormControl, FormControlLabel, Icon, Radio, RadioGroup } from "@mui/material";
import { ChangeEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { BodyText, BodyTextSmall } from "~/components/Typographies";
import NumberFieldBase from "~/components/textfield/NumberFieldBase";
import Config from "~/config";
import {
	describeOrientationPreviews,
	type OrientationPreview,
	toggleOrientationThreshold
} from "~/helpers/deviceOrientationPreview";
import { useAppSelector } from "~/hooks/reduxHooks";
import useDisplayModePreference from "~/hooks/useDisplayModePreference";
import useViewportMetrics from "~/hooks/useViewportMetrics";

const mockupMaxLongEdgePx = 120;

const iconOf = (isWideScreen: boolean) => (isWideScreen ? "desktop_windows" : "smartphone");

interface OrientationMockupProps {
	interactive: boolean;
	onToggle: () => void;
	preview: OrientationPreview;
}

const OrientationMockup = ({ interactive, onToggle, preview }: OrientationMockupProps) => {
	const { t } = useTranslation("common");
	const label = t(
		preview.orientation === "portrait"
			? "preferences.displayMode.orientationPortrait"
			: "preferences.displayMode.orientationLandscape"
	);
	const viewKey = preview.isWideScreen ? "preferences.displayMode.viewDesktop" : "preferences.displayMode.viewMobile";
	const otherViewKey = preview.isWideScreen
		? "preferences.displayMode.viewMobile"
		: "preferences.displayMode.viewDesktop";
	const view = t(viewKey);
	const icon = iconOf(preview.isWideScreen);
	const badgeColor = preview.isWideScreen ? "primary" : "info";

	return (
		<Box
			data-testid={`orientation-mockup-${preview.orientation}`}
			sx={{ alignItems: "center", display: "flex", flexDirection: "column", gap: 1, minWidth: 130 }}
		>
			<Box sx={{ alignItems: "center", display: "flex", gap: 0.75, minHeight: 24 }}>
				<BodyTextSmall bold={preview.isCurrent}>{label}</BodyTextSmall>
				{preview.isCurrent && (
					<Chip color="primary" label={t("preferences.displayMode.current")} size="small" variant="outlined" />
				)}
			</Box>

			<Box
				sx={{
					alignItems: "center",
					bgcolor: "action.hover",
					border: preview.isCurrent ? 2 : 1,
					borderColor: preview.isCurrent ? "primary.main" : "divider",
					borderRadius: 2,
					display: "flex",
					height: preview.mockup.heightPx,
					justifyContent: "center",
					width: preview.mockup.widthPx
				}}
			>
				<Icon sx={{ color: `${badgeColor}.main` }}>{icon}</Icon>
			</Box>

			<BodyTextSmall color="text.secondary">
				{t(preview.isCurrent ? "preferences.displayMode.pxReadout" : "preferences.displayMode.pxReadoutApprox", {
					width: preview.viewportWidthPx
				})}
			</BodyTextSmall>

			<Chip
				aria-label={
					interactive
						? t("preferences.displayMode.badgeInteractiveAria", { orientation: label, other: t(otherViewKey), view })
						: t("preferences.displayMode.badgeStaticAria", { orientation: label, view })
				}
				color={badgeColor}
				data-testid={`orientation-badge-${preview.orientation}`}
				icon={<Icon>{icon}</Icon>}
				label={view}
				onClick={interactive ? onToggle : undefined}
			/>
		</Box>
	);
};

const DisplayModeSettings = () => {
	const { t } = useTranslation("common");
	const [preference, savePreference] = useDisplayModePreference();
	const { currentOrientation, viewportHeight, viewportWidth } = useViewportMetrics();
	const isWideScreen = useAppSelector((state) => state.layout.isWideScreen);

	const defaultThreshold = Config.navDrawerCoverWidth;
	const effectiveThreshold = preference.autoThresholdPx ?? defaultThreshold;

	const [thresholdValue, setThresholdValue] = useState<null | number>(effectiveThreshold);

	useEffect(() => {
		setThresholdValue(effectiveThreshold);
	}, [effectiveThreshold]);

	const isAuto = preference.mode === "auto";

	const previews = describeOrientationPreviews({
		currentOrientation,
		defaultThreshold,
		maxLongEdgePx: mockupMaxLongEdgePx,
		preference,
		viewportHeight,
		viewportWidth
	});

	const handleModeChange = (_event: ChangeEvent<HTMLInputElement>, value: string) => {
		savePreference({ ...preference, mode: value as DisplayMode });
	};

	const setThreshold = (autoThresholdPx: number | undefined) => {
		savePreference({ autoThresholdPx, mode: "auto" });
	};

	const handleThresholdInput = (value: null | number) => {
		setThresholdValue(value);
		if (value !== null && value > 0) setThreshold(Math.trunc(value));
	};

	const toggleOrientation = (preview: OrientationPreview) => {
		setThreshold(toggleOrientationThreshold(preview.viewportWidthPx, preview.isWideScreen));
	};

	const currentViewLabel = t(
		isWideScreen ? "preferences.displayMode.viewDesktop" : "preferences.displayMode.viewMobile"
	);
	const overrideNote =
		preference.mode === "desktop"
			? t("preferences.displayMode.overrideDesktop")
			: t("preferences.displayMode.overrideMobile");

	return (
		<Box>
			<FormControl>
				<RadioGroup onChange={handleModeChange} value={preference.mode}>
					<FormControlLabel control={<Radio />} label={t("preferences.displayMode.modeAuto")} value="auto" />
					<FormControlLabel control={<Radio />} label={t("preferences.displayMode.modeDesktop")} value="desktop" />
					<FormControlLabel control={<Radio />} label={t("preferences.displayMode.modeMobile")} value="mobile" />
				</RadioGroup>
			</FormControl>

			<BodyTextSmall color="text.secondary" sx={{ mt: 1 }}>
				{t("preferences.displayMode.currentView", { view: currentViewLabel })}
			</BodyTextSmall>

			<Box sx={{ mt: 3 }}>
				<BodyText bold>{t("preferences.displayMode.bothOrientations")}</BodyText>
				<BodyTextSmall color="text.secondary" sx={{ mt: 0.5 }}>
					{t("preferences.displayMode.basedOnWindow", { height: viewportHeight, width: viewportWidth })}
					{isAuto ? t("preferences.displayMode.autoHint") : overrideNote}
				</BodyTextSmall>

				<Box sx={{ display: "flex", flexWrap: "wrap", gap: 4, mt: 2 }}>
					<OrientationMockup
						interactive={isAuto}
						onToggle={() => toggleOrientation(previews.portrait)}
						preview={previews.portrait}
					/>
					<OrientationMockup
						interactive={isAuto}
						onToggle={() => toggleOrientation(previews.landscape)}
						preview={previews.landscape}
					/>
				</Box>
			</Box>

			{isAuto && (
				<Box sx={{ mt: 3 }}>
					<BodyTextSmall color="text.secondary">
						{t("preferences.displayMode.autoExplain", { width: viewportWidth })}
					</BodyTextSmall>

					<Box sx={{ alignItems: "center", display: "flex", flexWrap: "wrap", gap: 2, mt: 2 }}>
						<NumberFieldBase
							ariaLabel={t("preferences.displayMode.switchWidthAria")}
							label={t("preferences.displayMode.switchWidthLabel")}
							min={1}
							onValueChange={handleThresholdInput}
							step={1}
							sx={{ width: 170 }}
							value={thresholdValue}
						/>
						<Button onClick={() => setThreshold(undefined)} size="small" variant="text">
							{t("preferences.displayMode.resetToDefault", { threshold: defaultThreshold })}
						</Button>
					</Box>

					<BodyTextSmall color="text.secondary" sx={{ mt: 2.5 }}>
						{t("preferences.displayMode.tabletTip")}
					</BodyTextSmall>
				</Box>
			)}
		</Box>
	);
};

export default DisplayModeSettings;
